<template>
  <q-dialog v-model="opened">
    <q-card style="width: 1100px; max-width: 95vw">
      <q-card-section class="row items-center">
        <strong>{{ $t('Product history') }}</strong><q-space />
        <q-btn flat icon="refresh" :loading="loading" @click="load" />
        <q-btn flat icon="close" v-close-popup />
      </q-card-section>
      <q-card-section>
        <p>{{ $t('Older operations may have incomplete history.') }}</p>
        <q-banner v-if="error" class="bg-red-1">{{ error }}</q-banner>
        <q-table :rows="rows" :columns="columns" row-key="id" :loading="loading"
          v-model:pagination="pagination" :rows-per-page-options="[25, 50, 100]" @request="request">
          <template #body-cell-order="props">
            <q-td :props="props">
              <router-link v-if="orderRoute(props.row)" :to="orderRoute(props.row)" target="_blank" rel="noopener noreferrer">
                {{ props.row.outboundOrder.id }}
              </router-link>
              <span v-else>{{ props.row.type === 'shipping' ? $t('Unknown') : '—' }}</span>
            </q-td>
          </template>
          <template #body-cell-details="props">
            <q-td :props="props"><div v-for="(change, field) in props.row.auditContext?.changes || {}" :key="field">
              {{ fieldLabel(field) }}: {{ display(change.before) }} → {{ display(change.after) }}
            </div>
            <div v-if="props.row.type !== 'document_change'">
              {{ props.row.fromPlaceId ?? props.row.from_place_id ?? '—' }} → {{ props.row.placeId ?? props.row.place_id ?? '—' }}
              · {{ props.row.fromState ?? props.row.from_state ?? '—' }} → {{ props.row.toState ?? props.row.to_state ?? '—' }}
            </div></q-td>
          </template>
        </q-table>
        <slot />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
<script>
export default {
  name: 'DocumentHistory',
  props: { documentId: { type: [Number, String], default: null }, itemId: { type: [Number, String], default: null }, offerId: { type: [Number, String], default: null }, shopId: { type: [Number, String], default: null } },
  data () {
    return {
      opened: false, loading: false, error: '', rows: [], generation: 0,
      pagination: { page: 1, rowsPerPage: 25, rowsNumber: 0 },
      columns: [
        { name: 'time', label: this.$t('Date'), field: row => this.$moment(row.auditContext?.occurredAt || row.created).format('DD.MM.YYYY HH:mm:ss Z'), align: 'left' },
        { name: 'type', label: this.$t('Action'), field: row => row.auditContext?.action || row.type, align: 'left' },
        { name: 'offer', label: this.$t('Product'), field: row => (row.auditContext?.offer?.shop || row.shopId || row.shop_id || '—') + ' / ' + (row.auditContext?.offer?.id || row.productOfferId || row.productoffer_id || '—'), align: 'left' },
        { name: 'item', label: this.$t('Item'), field: row => row.auditContext?.documentItemId, align: 'left' },
        { name: 'order', label: this.$t('Outbound order'), field: row => row.outboundOrder?.id, align: 'left' },
        { name: 'actor', label: this.$t('User'), field: row => row.auditContext?.actorName || row.executiveId || row.executive_id || this.$t('Unknown'), align: 'left' },
        { name: 'source', label: this.$t('Source'), field: row => row.auditContext?.reportedSource ? `${row.auditContext.source} / ${row.auditContext.reportedSource}` : row.auditContext?.source || this.$t('Unknown'), align: 'left' },
        { name: 'count', label: this.$t('Count'), field: row => row.type === 'document_change' ? '—' : row.count },
        { name: 'details', label: this.$t('Changes'), align: 'left' }
      ]
    }
  },
  watch: { documentId () { this.generation++; this.rows = []; this.pagination.page = 1; if (this.opened) this.load() } },
  methods: {
    orderRoute (row) {
      const order = row.type === 'shipping' && row.outboundOrder
      if (!order || !/^[1-9]\d*$/.test(String(order.id)) || !/^[a-zA-Z][a-zA-Z0-9]*$/.test(order.type || '')) return null
      return `/outbound/orders/entity/${order.type}/${order.id}`
    },
    open () { this.opened = true; this.pagination.page = 1; return this.load() },
    request ({ pagination }) { this.pagination = pagination; return this.load() },
    fieldLabel (field) { return this.$t(({ quantityExpected: 'Expected', quantityReal: 'Received', quantityPlace: 'Allocated', quantityShipped: 'Shipped', state: 'State', document: 'Document', productOffer: 'Product', shop: 'Shop', warehouse: 'Warehouse' })[field] || field) },
    display (value) { return value === null ? '—' : typeof value === 'object' ? JSON.stringify(value) : value },
    async load () {
      const generation = ++this.generation
      this.loading = true; this.error = ''
      try {
        const identity = (subfield, value) => ({ type: 'jsonboperator', field: 'auditContext', subfield, subtype: 'eq', value, native: true })
        const filter = this.documentId
          ? [identity('documentId', this.documentId), ...(this.itemId ? [identity('documentItemId', this.itemId)] : [])]
          : [{ type: 'jsonb', property: 'auditContext', field: 'offer', condition: 'eq', value: { id: this.offerId, shop: this.shopId } }]
        const result = await this.$service.locationLog.getAll({ forceReload: true, filter, page: this.pagination.page, per_page: this.pagination.rowsPerPage })
        if (generation !== this.generation) return
        if (result.items.some(row => !Object.prototype.hasOwnProperty.call(row, 'auditContext'))) {
          throw new Error(this.$t('Product history is not available on this server yet.'))
        }
        this.rows = result.items
        this.pagination.rowsNumber = result.totalItems
      } catch (error) {
        if (generation !== this.generation) return
        this.rows = []; this.error = error.message || this.$t('Unable to load history')
      } finally { if (generation === this.generation) this.loading = false }
    }
  }
}
</script>
